import React, { useEffect, useMemo, useRef } from 'react';
import 'swiper/scss';
import './style.scss';
import { Swiper, SwiperSlide } from 'swiper/react';
import NftIcon from '../../../../../general/nft/icon';
import { Swiper as ISwiper } from 'swiper/types';
import { API_OwnNft, NFTBgColors } from 'shared';
import clsx from 'clsx';
import NftsCarouselArrow from '../arrow';
import useWindowSize from '../../../../../../hooks/useWindowSize';

interface Props {
  nfts: API_OwnNft[];
  activeIndex: number;
  setActiveIndex: (newIndex: number) => void;
  horizontal?: boolean;
}

const OwnNftsCarouselSwiper = ({
  nfts,
  activeIndex,
  setActiveIndex,
  horizontal
}: Props) => {
  const initialized = useRef<boolean>(false);
  const swiperRef = useRef<ISwiper | undefined>(undefined);
  const [sizeX] = useWindowSize();

  useEffect(() => {
    if (swiperRef.current && activeIndex !== swiperRef.current.realIndex) {
      swiperRef.current.slideToLoop(activeIndex);
    }
  }, [activeIndex]);

  const initialize = (swiper: ISwiper) => {
    initialized.current = true;
    swiperRef.current = swiper;
    swiper.slideToLoop(activeIndex);
  };

  const handleSlideChange = (swiper: ISwiper) => {
    if (initialized.current) {
      swiperRef.current = swiper;
      setActiveIndex(swiper.realIndex);
    }
  };

  const props = useMemo(() => {
    const minified = nfts.length <= 2;
    const slideSize = sizeX > 650 ? 90 : 75;
    const slidesPerView = minified ? 2 : 3;
    return {
      slidesPerView,
      height: (slideSize + 26) * slidesPerView,
      width: (slideSize + 26) * slidesPerView,
      style: {
        maxHeight: horizontal
          ? undefined
          : (slideSize + 26) * slidesPerView - 13,
        maxWidth: horizontal ? (slideSize + 26) * slidesPerView - 13 : undefined
      },
      centeredSlides: !minified
    };
  }, [nfts?.length, sizeX > 650, horizontal]);

  return (
    <div className='carousel-swiper-wrapper'>
      <Swiper
        spaceBetween={0}
        {...props}
        direction={horizontal ? 'horizontal' : 'vertical'}
        className='carousel-swiper'
        loop
        onSlideChange={(swiper) => handleSlideChange(swiper)}
        slideToClickedSlide={true}
        onSwiper={(swiper) => initialize(swiper)}
      >
        {nfts.map((nft) => (
          <SwiperSlide key={nft._id}>
            <NftIcon data={nft} />
          </SwiperSlide>
        ))}
      </Swiper>
      <Arrows
        color={nfts[activeIndex].colors.background}
        horizontal={horizontal}
      />
    </div>
  );
};

interface ArrowProps {
  color: NFTBgColors;
  horizontal?: boolean;
}

const Arrows = ({ color, horizontal }: ArrowProps) => (
  <div className={clsx('carousel-arrows', horizontal && 'horizontal')}>
    <NftsCarouselArrow
      color={color}
      direction={horizontal ? 'bottom' : 'right'}
      size={20}
    />
    <NftsCarouselArrow
      color={color}
      direction={horizontal ? 'top' : 'left'}
      size={20}
    />
  </div>
);

export default OwnNftsCarouselSwiper;
